/**
 * Iter 113 — Reports modal (dispatcher analytics).
 *
 * Scoped to the caller's HOME agency (assigned at account creation).
 * Every section (Call Type Breakdown, Calls Assigned per Unit, Average
 * Response Time) has its own independent Today / Week / Month / Year
 * picker. The top-of-modal range is a default; per-section overrides
 * win when set.
 *
 * PDF export is now WHITE-BACKGROUND with per-section pagination.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import {
  BarChart, Bar, PieChart, Pie, LineChart, Line, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend,
} from "recharts";
import { MapContainer, TileLayer, useMap } from "react-leaflet";
import L from "leaflet";
import "leaflet.heat";
import "leaflet/dist/leaflet.css";
import { STADIA_TILE_URL } from "@/components/dispatch/utils";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PALETTE = [
  "#0077CC", "#E6A700", "#008847", "#D95E1E", "#C22040",
  "#7B4FBB", "#4A80B8", "#B85555", "#5A8A33", "#1A96A8",
  "#B33636", "#CC7A22", "#3E8C48", "#2E7BC6", "#8A3AA0",
];

const RANGES = [
  { id: "day", label: "Today" },
  { id: "week", label: "This Week" },
  { id: "month", label: "This Month" },
  { id: "year", label: "This Year" },
  { id: "all_time", label: "All Time" },
];

const SECTION_RANGES = [
  { id: "day", label: "Today" },
  { id: "week", label: "Week" },
  { id: "month", label: "Month" },
  { id: "year", label: "Year" },
];

function fmtSecs(s) {
  if (!s) return "—";
  const m = Math.floor(s / 60);
  const r = s % 60;
  return m ? `${m}m ${r}s` : `${r}s`;
}

function ChartPicker({ value, onChange }) {
  return (
    <div style={{ display: "flex", gap: 6 }}>
      {["bar", "pie", "line"].map((t) => (
        <button
          key={t}
          data-testid={`chart-type-${t}`}
          onClick={() => onChange(t)}
          style={{
            padding: "3px 10px",
            fontSize: 11,
            fontWeight: 700,
            letterSpacing: 1,
            background: value === t ? "#00BFFF" : "transparent",
            color: value === t ? "#000" : "#00BFFF",
            border: "1px solid #00BFFF",
            borderRadius: 3,
            cursor: "pointer",
            textTransform: "uppercase",
          }}
        >
          {t}
        </button>
      ))}
    </div>
  );
}

/**
 * Section-scoped range picker (Today / Week / Month / Year). Each report
 * section owns its own state; chiefs asked for independent time windows
 * per section so one report can mix "Call types this month" with "Unit
 * workload this year".
 */
function SectionRangePicker({ value, onChange, testid }) {
  return (
    <div style={{ display: "flex", gap: 4 }} data-testid={testid}>
      {SECTION_RANGES.map((r) => (
        <button
          key={r.id}
          data-testid={`${testid}-${r.id}`}
          onClick={() => onChange(r.id)}
          style={{
            padding: "3px 9px",
            fontSize: 10,
            fontWeight: 700,
            letterSpacing: 1,
            background: value === r.id ? "#00BFFF" : "transparent",
            color: value === r.id ? "#000" : "#00BFFF",
            border: "1px solid #00BFFF",
            borderRadius: 3,
            cursor: "pointer",
            textTransform: "uppercase",
          }}
        >
          {r.label}
        </button>
      ))}
    </div>
  );
}

function MultiSelectFilter({ label, options, selected, onChange, testid }) {
  const [open, setOpen] = useState(false);
  const isAll = selected.length === 0;
  const summary = isAll
    ? "ALL"
    : selected.length === 1 ? selected[0] : `${selected.length} SELECTED`;
  const toggle = (v) => {
    onChange(selected.includes(v) ? selected.filter((s) => s !== v) : [...selected, v]);
  };
  return (
    <div style={{ position: "relative" }} data-testid={testid}>
      <button
        data-testid={`${testid}-trigger`}
        onClick={() => setOpen((v) => !v)}
        style={{
          background: isAll ? "transparent" : "rgba(0,191,255,0.15)",
          color: "#00BFFF",
          border: "1px solid #00BFFF", borderRadius: 3,
          padding: "5px 12px", fontSize: 11, fontWeight: 700, letterSpacing: 1,
          cursor: "pointer", textTransform: "uppercase",
        }}
      >
        {label}: {summary} {open ? "▲" : "▼"}
      </button>
      {open && (
        <div data-testid={`${testid}-panel`}
          style={{
            position: "absolute", top: "calc(100% + 4px)", right: 0,
            background: "#0d0d0d", border: "1px solid #333", borderRadius: 4,
            zIndex: 50, minWidth: 220, maxHeight: 320, overflowY: "auto",
            padding: 8, boxShadow: "0 8px 24px rgba(0,0,0,0.6)",
          }}
        >
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6, paddingBottom: 6, borderBottom: "1px solid #222" }}>
            <button data-testid={`${testid}-all`}
              onClick={() => onChange([])}
              style={{ background: "transparent", color: "#FFCC00", border: "1px solid #FFCC00", borderRadius: 3, padding: "3px 10px", fontSize: 10, fontWeight: 700, letterSpacing: 1, cursor: "pointer" }}
            >
              CLEAR (ALL)
            </button>
            <button
              onClick={() => setOpen(false)}
              style={{ background: "transparent", color: "#888", border: "none", cursor: "pointer", fontSize: 12 }}
            >
              ✕
            </button>
          </div>
          {options.length === 0 && <div style={{ color: "#666", fontSize: 11, padding: 6 }}>No options yet — no jobs in this agency.</div>}
          {options.map((opt) => (
            <label key={opt} style={{ display: "flex", alignItems: "center", gap: 8, padding: "4px 6px", cursor: "pointer", fontSize: 12, color: selected.includes(opt) ? "#00BFFF" : "#CCC", borderRadius: 3 }}
              onMouseEnter={(e) => (e.currentTarget.style.background = "rgba(255,255,255,0.05)")}
              onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
            >
              <input type="checkbox" checked={selected.includes(opt)} onChange={() => toggle(opt)} style={{ accentColor: "#00BFFF" }} />
              {opt}
            </label>
          ))}
        </div>
      )}
    </div>
  );
}


function CategoricalChart({ data, chartType, testid }) {
  if (!data || data.length === 0) {
    return <div data-testid={`${testid}-empty`} style={{ padding: 24, color: "#666", fontSize: 12, textAlign: "center" }}>No data for this range.</div>;
  }
  if (chartType === "pie") {
    return (
      <ResponsiveContainer width="100%" height={260}>
        <PieChart data-testid={testid}>
          <Pie data={data} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={90} label>
            {data.map((_, i) => <Cell key={i} fill={PALETTE[i % PALETTE.length]} />)}
          </Pie>
          <Tooltip contentStyle={{ background: "#111", border: "1px solid #333", color: "#EEE" }} />
          <Legend wrapperStyle={{ color: "#CCC", fontSize: 11 }} />
        </PieChart>
      </ResponsiveContainer>
    );
  }
  if (chartType === "line") {
    return (
      <ResponsiveContainer width="100%" height={260}>
        <LineChart data={data} data-testid={testid}>
          <CartesianGrid stroke="#222" />
          <XAxis dataKey="name" tick={{ fill: "#AAA", fontSize: 10 }} interval={0} angle={-30} textAnchor="end" height={70} />
          <YAxis tick={{ fill: "#AAA", fontSize: 10 }} allowDecimals={false} />
          <Tooltip contentStyle={{ background: "#111", border: "1px solid #333", color: "#EEE" }} />
          <Line type="monotone" dataKey="value" stroke="#00BFFF" strokeWidth={2} dot={{ fill: "#00BFFF" }} />
        </LineChart>
      </ResponsiveContainer>
    );
  }
  return (
    <ResponsiveContainer width="100%" height={260}>
      <BarChart data={data} data-testid={testid}>
        <CartesianGrid stroke="#222" />
        <XAxis dataKey="name" tick={{ fill: "#AAA", fontSize: 10 }} interval={0} angle={-30} textAnchor="end" height={70} />
        <YAxis tick={{ fill: "#AAA", fontSize: 10 }} allowDecimals={false} />
        <Tooltip contentStyle={{ background: "#111", border: "1px solid #333", color: "#EEE" }} />
        <Bar dataKey="value">
          {data.map((_, i) => <Cell key={i} fill={PALETTE[i % PALETTE.length]} />)}
        </Bar>
      </BarChart>
    </ResponsiveContainer>
  );
}

function HeatmapLayer({ points }) {
  const map = useMap();
  const layerRef = useRef(null);
  useEffect(() => {
    if (!map) return;
    if (layerRef.current) {
      layerRef.current.remove();
      layerRef.current = null;
    }
    if (!points || points.length === 0) return;
    try {
      const latlngs = points.map((p) => [p[0], p[1]]);
      map.fitBounds(latlngs, { padding: [20, 20], maxZoom: 14 });
    } catch { /* ignore */ }
    layerRef.current = L.heatLayer(points, {
      radius: 22,
      blur: 18,
      maxZoom: 17,
      minOpacity: 0.35,
      gradient: { 0.2: "#00BFFF", 0.4: "#00CC66", 0.6: "#FFCC00", 0.8: "#FF7030", 1: "#FF3366" },
    });
    layerRef.current.addTo(map);
    return () => {
      if (layerRef.current) { layerRef.current.remove(); layerRef.current = null; }
    };
  }, [map, points]);
  return null;
}

/**
 * Iter 114 — Response Time section renders (1) a big agency-wide
 * average tile at the top, and (2) an OPTIONAL per-unit breakdown that
 * only appears once the operator picks specific units. This keeps the
 * default view clean and puts the agency KPI front-and-center.
 */
function ResponseTimeBlock({ src, allUnits, selectedUnits, testid }) {
  const avg = src?.avg_response_seconds || 0;
  const total = src?.response_count || 0;
  const perUnit = src?.by_unit_response || [];
  const anyResponse = perUnit.some((r) => r.count > 0);
  const filtered = selectedUnits.length
    ? perUnit.filter((r) => selectedUnits.includes(r.name))
    : [];
  return (
    <div data-testid={testid} style={{ padding: 4 }}>
      {/* Agency-wide average tile */}
      <div style={{
        display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 12,
      }}>
        <div data-testid={`${testid}-agency-avg`} style={{
          background: "#141414", border: "1px solid #222", borderRadius: 4,
          padding: "18px 20px",
        }}>
          <div style={{ fontSize: 10, color: "#888", letterSpacing: 2, textTransform: "uppercase" }}>
            Agency Average
          </div>
          <div style={{ fontSize: 34, color: "#00BFFF", fontWeight: 800, marginTop: 4, lineHeight: 1 }}>
            {avg ? fmtSecs(avg) : "—"}
          </div>
          <div style={{ fontSize: 11, color: "#666", marginTop: 6 }}>
            {anyResponse ? "Dispatched → on-scene, all agency calls in range" : "No response data recorded for this range."}
          </div>
        </div>
        <div data-testid={`${testid}-agency-count`} style={{
          background: "#141414", border: "1px solid #222", borderRadius: 4,
          padding: "18px 20px",
        }}>
          <div style={{ fontSize: 10, color: "#888", letterSpacing: 2, textTransform: "uppercase" }}>
            Qualifying Calls
          </div>
          <div style={{ fontSize: 34, color: "#00BFFF", fontWeight: 800, marginTop: 4, lineHeight: 1 }}>
            {total}
          </div>
          <div style={{ fontSize: 11, color: "#666", marginTop: 6 }}>
            Total calls counted in agency-wide average.
          </div>
        </div>
      </div>

      {/* Optional per-unit breakdown — only when units are selected */}
      {selectedUnits.length > 0 && (
        <div data-testid={`${testid}-per-unit-block`}>
          <div style={{ fontSize: 11, color: "#888", letterSpacing: 2, textTransform: "uppercase", marginBottom: 6, paddingLeft: 4 }}>
            Selected Units ({selectedUnits.length})
          </div>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
            <thead>
              <tr style={{ color: "#CCC", textAlign: "left" }}>
                <th style={{ padding: "6px 10px", borderBottom: "1px solid #333" }}>Unit</th>
                <th style={{ padding: "6px 10px", borderBottom: "1px solid #333", textAlign: "right" }}>Avg Response</th>
                <th style={{ padding: "6px 10px", borderBottom: "1px solid #333", textAlign: "right" }}>Calls</th>
              </tr>
            </thead>
            <tbody>
              {selectedUnits.map((name) => {
                const row = filtered.find((r) => r.name === name) || { name, avg_seconds: 0, count: 0 };
                return (
                  <tr key={name} data-testid={`${testid}-row-${name}`}>
                    <td style={{ padding: "6px 10px", borderBottom: "1px solid #1a1a1a", color: "#EEE", fontFamily: "monospace" }}>{row.name}</td>
                    <td style={{ padding: "6px 10px", borderBottom: "1px solid #1a1a1a", color: row.count ? "#00BFFF" : "#555", textAlign: "right", fontWeight: 700 }}>
                      {row.count ? fmtSecs(row.avg_seconds) : "—"}
                    </td>
                    <td style={{ padding: "6px 10px", borderBottom: "1px solid #1a1a1a", color: "#CCC", textAlign: "right" }}>{row.count}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
      {selectedUnits.length === 0 && (
        <div data-testid={`${testid}-hint`} style={{ padding: "10px 4px", color: "#666", fontSize: 11, fontStyle: "italic" }}>
          Pick one or more units above to see their individual average response times.
        </div>
      )}
    </div>
  );
}

/**
 * Iter 113 — Renders a single section's canvas to PDF with a WHITE
 * background, a black title header "<Agency> — <Section Title>", the
 * date range on the second line, and the section body. Used both for
 * per-section PDF buttons and each page of the combined PDF.
 */
async function renderSectionToPdf(pdf, node, { title, agency, rangeLabel, addPageBefore }) {
  const canvas = await html2canvas(node, {
    backgroundColor: "#ffffff",
    scale: 2,
    useCORS: true,
    foreignObjectRendering: false,
    logging: false,
    // Iter 114 — convert the captured section clone to a light theme so
    // the PDF renders on a true white background (originals stay dark).
    // Leaflet map tiles and heat overlay are excluded via the
    // `.leaflet-container` gate so streets + call locations stay
    // visible on the heatmap page.
    onclone: (doc, cloned) => {
      cloned.setAttribute("data-report-pdf-clone", "true");
      const style = doc.createElement("style");
      style.textContent = `
        [data-report-pdf-clone], [data-report-pdf-clone] * {
          background-color: #ffffff !important;
          background-image: none !important;
          color: #0a1e3c !important;
          border-color: #cccccc !important;
          box-shadow: none !important;
        }
        [data-report-pdf-clone] table th,
        [data-report-pdf-clone] table td { color: #0a1e3c !important; }
        [data-report-pdf-clone] .recharts-text tspan,
        [data-report-pdf-clone] .recharts-cartesian-axis-tick text,
        [data-report-pdf-clone] .recharts-legend-item-text { fill: #0a1e3c !important; color: #0a1e3c !important; }
        [data-report-pdf-clone] .recharts-cartesian-grid line { stroke: #dddddd !important; }
        [data-report-pdf-clone] button { display: none !important; }
        /* Leaflet: keep the map dark so streets + heat markers stay visible */
        [data-report-pdf-clone] .leaflet-container,
        [data-report-pdf-clone] .leaflet-container *,
        [data-report-pdf-clone] .leaflet-pane,
        [data-report-pdf-clone] .leaflet-pane * {
          background-color: transparent !important;
          background-image: unset !important;
          color: unset !important;
        }
        [data-report-pdf-clone] .leaflet-container {
          background-color: #000000 !important;
        }
        [data-report-pdf-clone] .leaflet-control-container { display: none !important; }
      `;
      doc.head.appendChild(style);
    },
  });
  const imgData = canvas.toDataURL("image/jpeg", 0.9);
  const pageW = pdf.internal.pageSize.getWidth();
  const pageH = pdf.internal.pageSize.getHeight();
  const margin = 10;
  const usableW = pageW - margin * 2;
  const headerH = 24;
  if (addPageBefore) pdf.addPage();
  // Solid white page background
  pdf.setFillColor(255, 255, 255);
  pdf.rect(0, 0, pageW, pageH, "F");
  // Header: agency + section title in navy, generated stamp in gray
  pdf.setTextColor(10, 30, 60);
  pdf.setFontSize(16);
  pdf.setFont(undefined, "bold");
  pdf.text(`${agency} — ${title}`, margin, 14);
  pdf.setFont(undefined, "normal");
  pdf.setTextColor(90, 90, 90);
  pdf.setFontSize(9);
  pdf.text(
    `Range: ${rangeLabel}   ·   Generated: ${new Date().toLocaleString("en-US", { timeZone: "America/New_York" })} (NY)`,
    margin, 20,
  );
  // Thin divider
  pdf.setDrawColor(200, 200, 200);
  pdf.line(margin, 22, pageW - margin, 22);
  // Body image, preserving aspect
  const imgHmm = (canvas.height * usableW) / canvas.width;
  const bodyMax = pageH - headerH - margin;
  const fitH = Math.min(imgHmm, bodyMax);
  const fitW = (fitH / imgHmm) * usableW;
  pdf.addImage(imgData, "JPEG", margin, headerH, fitW, fitH);
}

export default function ReportsModal({ user, onClose }) {
  const homeAgency = user?.home_agency || user?.agency || (user?.agencies || [])[0] || "";
  const [range, setRange] = useState("day");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const [ctType, setCtType] = useState("bar");
  const [unitType, setUnitType] = useState("bar");
  const printRef = useRef(null);
  const [exporting, setExporting] = useState(false);
  const sectionRefs = {
    calltypes: useRef(null),
    byunit: useRef(null),
    response: useRef(null),
    heatmap: useRef(null),
  };
  const _todayNY = new Date().toLocaleDateString("en-CA", { timeZone: "America/New_York" });
  const _30daysAgo = (() => {
    const d = new Date();
    d.setDate(d.getDate() - 30);
    return d.toLocaleDateString("en-CA", { timeZone: "America/New_York" });
  })();
  const [customStart, setCustomStart] = useState(_30daysAgo);
  const [customEnd, setCustomEnd] = useState(_todayNY);
  const [applyTick, setApplyTick] = useState(0);
  const [orphanCount, setOrphanCount] = useState(0);
  const [backfillMsg, setBackfillMsg] = useState("");
  const refreshOrphanCount = useCallback(() => {
    axios.get(`${API}/reports/orphan-count`)
      .then((r) => setOrphanCount(r.data?.empty_agency_total || 0))
      .catch(() => {});
  }, []);
  useEffect(() => { refreshOrphanCount(); }, [refreshOrphanCount, applyTick]);
  const [audit, setAudit] = useState(null);
  const refreshAudit = useCallback(() => {
    axios.get(`${API}/reports/agency-audit`)
      .then((r) => setAudit(r.data))
      .catch(() => {});
  }, []);
  useEffect(() => {
    if (data && data.total_calls === 0) refreshAudit();
  }, [data, refreshAudit]);
  const runRename = async (fromAgency) => {
    if (!homeAgency) return;
    if (!window.confirm(`Reassign every job currently labeled "${fromAgency || "(null)"}" to "${homeAgency}"?`)) return;
    try {
      const r = await axios.post(`${API}/reports/rename-agency`, {
        from_agency: fromAgency,
        to_agency: homeAgency,
      });
      if (r.data?.error) { alert("Error: " + r.data.error); return; }
      alert(`Renamed ${r.data.modified} job(s). Refreshing…`);
      setApplyTick((n) => n + 1);
      refreshAudit();
    } catch (e) {
      alert("Error: " + (e?.response?.data?.error || e.message));
    }
  };
  const runBackfill = async () => {
    if (!homeAgency) return;
    if (!window.confirm(`Attribute ${orphanCount} unassigned old call(s) to "${homeAgency}"? This cannot be undone.`)) return;
    setBackfillMsg("Attributing…");
    try {
      const r = await axios.post(`${API}/reports/backfill-agency`, { agency: homeAgency, only_no_unit: false });
      if (r.data?.error) {
        setBackfillMsg("Error: " + r.data.error);
      } else {
        setBackfillMsg(`${r.data.modified} calls attributed to ${homeAgency}. Refreshing…`);
        setApplyTick((n) => n + 1);
      }
    } catch (e) {
      setBackfillMsg("Error: " + (e?.response?.data?.error || e.message));
    }
  };
  const [ctPick, setCtPick] = useState("ALL");
  const [unitPick, setUnitPick] = useState("ALL");
  const [ctSectionUnit, setCtSectionUnit] = useState("ALL");
  // Iter 114 — response section now uses a MULTI-SELECT of units.
  // Empty array = agency-wide only (default). Picking units reveals a
  // per-unit table below the agency KPI tiles.
  const [respSectionUnits, setRespSectionUnits] = useState([]);
  // Iter 113 — Per-section date filters. Each section is independent.
  const [ctSectionRange, setCtSectionRange] = useState("day");
  const [unitSectionRange, setUnitSectionRange] = useState("day");
  const [respSectionRange, setRespSectionRange] = useState("day");
  const [ctSectionData, setCtSectionData] = useState(null);
  const [unitSectionData, setUnitSectionData] = useState(null);
  const [respSectionData, setRespSectionData] = useState(null);
  const [matrix, setMatrix] = useState({ call_type_matrix: [], unit_matrix: [] });
  useEffect(() => {
    if (!homeAgency) return;
    axios.get(`${API}/reports/matrix`, { params: { agency: homeAgency } })
      .then((r) => setMatrix({
        call_type_matrix: r.data?.call_type_matrix || [],
        unit_matrix: r.data?.unit_matrix || [],
      }))
      .catch(() => {});
  }, [homeAgency, applyTick]);
  const [filterOptions, setFilterOptions] = useState({ call_types: [], units: [] });
  const [selectedCallTypes, setSelectedCallTypes] = useState([]);
  const [selectedUnits, setSelectedUnits] = useState([]);
  useEffect(() => {
    if (!homeAgency) return;
    axios.get(`${API}/reports/filter-options`, { params: { agency: homeAgency } })
      .then((r) => setFilterOptions({
        call_types: r.data?.call_types || [],
        units: r.data?.units || [],
      }))
      .catch(() => {});
  }, [homeAgency]);

  const fetchReport = useCallback(async () => {
    if (!homeAgency) {
      setErr("Your account has no home agency assigned — cannot run reports. Contact an admin.");
      return;
    }
    setLoading(true);
    setErr("");
    try {
      const params = { agency: homeAgency, range };
      if (range === "custom") {
        if (!customStart || !customEnd) { setErr("Pick a start and end date."); setLoading(false); return; }
        if (customEnd < customStart) { setErr("End date must be on or after start date."); setLoading(false); return; }
        params.start = customStart;
        params.end = customEnd;
      }
      if (selectedCallTypes.length) params.call_types = selectedCallTypes.join(",");
      if (selectedUnits.length) params.units = selectedUnits.join(",");
      const r = await axios.get(`${API}/reports/dispatch`, { params });
      if (r.data?.error) setErr(r.data.error);
      else setData(r.data);
    } catch (e) {
      setErr(e?.response?.data?.error || e.message || "Failed to load report");
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [homeAgency, range, applyTick, selectedCallTypes, selectedUnits]);

  useEffect(() => { fetchReport(); }, [fetchReport]);

  // Iter 113 — Section-scoped fetches. Fired whenever a section's own
  // range or unit filter changes. Skips network only when both are
  // defaults matching the top-level range.
  useEffect(() => {
    if (!homeAgency) return;
    const params = { agency: homeAgency, range: ctSectionRange };
    if (ctSectionUnit !== "ALL") params.units = ctSectionUnit;
    axios.get(`${API}/reports/dispatch`, { params })
      .then((r) => setCtSectionData(r.data)).catch(() => setCtSectionData(null));
  }, [ctSectionUnit, ctSectionRange, homeAgency, applyTick]);

  useEffect(() => {
    if (!homeAgency) return;
    const params = { agency: homeAgency, range: unitSectionRange };
    axios.get(`${API}/reports/dispatch`, { params })
      .then((r) => setUnitSectionData(r.data)).catch(() => setUnitSectionData(null));
  }, [unitSectionRange, homeAgency, applyTick]);

  useEffect(() => {
    if (!homeAgency) return;
    const params = { agency: homeAgency, range: respSectionRange };
    // Iter 114 — Do NOT scope the response fetch to selected units; the
    // agency-wide average always reflects the FULL agency roster. The
    // frontend filters the per-unit table locally from `by_unit_response`.
    axios.get(`${API}/reports/dispatch`, { params })
      .then((r) => setRespSectionData(r.data)).catch(() => setRespSectionData(null));
  }, [respSectionRange, homeAgency, applyTick]);

  // Effective source data per section (falls back to top-level if
  // section fetch has not resolved yet).
  const ctSrc = ctSectionData || data;
  const unitSrc = unitSectionData || data;
  const respSrc = respSectionData || data;

  const ctRangeLabel = useMemo(() => (SECTION_RANGES.find(r => r.id === ctSectionRange) || {}).label || ctSectionRange, [ctSectionRange]);
  const unitRangeLabel = useMemo(() => (SECTION_RANGES.find(r => r.id === unitSectionRange) || {}).label || unitSectionRange, [unitSectionRange]);
  const respRangeLabel = useMemo(() => (SECTION_RANGES.find(r => r.id === respSectionRange) || {}).label || respSectionRange, [respSectionRange]);
  const topRangeLabel = useMemo(() => (RANGES.find(r => r.id === range) || {}).label || range, [range]);

  /**
   * Iter 113 — Combined PDF (white background, section-per-page).
   * Order: Call Type Breakdown → Calls Assigned per Unit → Avg Response
   *        → Heatmap. Each page has its own agency+title header and the
   * range label reflects that section's independent picker.
   */
  const handleExportPdf = useCallback(async () => {
    if (exporting) return;
    setExporting(true);
    try {
      await new Promise((r) => requestAnimationFrame(() => setTimeout(r, 200)));
      const pdf = new jsPDF({ orientation: "landscape", unit: "mm", format: "a4" });
      const sections = [
        { key: "calltypes", title: "Call Type Breakdown", rangeLabel: ctRangeLabel },
        { key: "byunit",    title: "Calls Assigned per Unit", rangeLabel: unitRangeLabel },
        { key: "response",  title: "Average Response Time", rangeLabel: respRangeLabel },
        { key: "heatmap",   title: "Call Location Heatmap", rangeLabel: topRangeLabel },
      ];
      let first = true;
      for (const s of sections) {
        const node = sectionRefs[s.key]?.current;
        if (!node) continue;
        await renderSectionToPdf(pdf, node, {
          title: s.title,
          agency: homeAgency,
          rangeLabel: s.rangeLabel,
          addPageBefore: !first,
        });
        first = false;
      }
      // Footer on every page
      const pageCount = pdf.internal.getNumberOfPages();
      const pageW = pdf.internal.pageSize.getWidth();
      const pageH = pdf.internal.pageSize.getHeight();
      for (let p = 1; p <= pageCount; p++) {
        pdf.setPage(p);
        pdf.setTextColor(140, 140, 140);
        pdf.setFontSize(8);
        pdf.text(`Page ${p} of ${pageCount}`, pageW - 30, pageH - 5);
        pdf.text("Volunteer EMS Comms — internal use", 10, pageH - 5);
      }
      const safeAgency = (homeAgency || "agency").replace(/[^a-z0-9]+/gi, "-").slice(0, 40).toLowerCase();
      const stamp = new Date().toISOString().replace(/[:.]/g, "-").slice(0, 16);
      pdf.save(`dispatch-report_${safeAgency}_${stamp}.pdf`);
    } catch (e) {
      console.error("PDF export failed", e);
      alert("PDF export failed: " + (e?.message || "unknown error"));
    } finally {
      setExporting(false);
    }
  }, [exporting, homeAgency, ctRangeLabel, unitRangeLabel, respRangeLabel, topRangeLabel]);

  const handleExportSectionPdf = useCallback(async (sectionKey, sectionTitle, rangeLabel) => {
    const node = sectionRefs[sectionKey]?.current;
    if (!node || exporting) return;
    setExporting(true);
    try {
      await new Promise((r) => requestAnimationFrame(() => setTimeout(r, 150)));
      const pdf = new jsPDF({ orientation: "landscape", unit: "mm", format: "a4" });
      await renderSectionToPdf(pdf, node, {
        title: sectionTitle,
        agency: homeAgency,
        rangeLabel,
        addPageBefore: false,
      });
      const pageW = pdf.internal.pageSize.getWidth();
      const pageH = pdf.internal.pageSize.getHeight();
      pdf.setTextColor(140, 140, 140);
      pdf.setFontSize(8);
      pdf.text("Volunteer EMS Comms — internal use", 10, pageH - 5);
      pdf.text("Page 1 of 1", pageW - 30, pageH - 5);
      const safeAgency = (homeAgency || "agency").replace(/[^a-z0-9]+/gi, "-").slice(0, 40).toLowerCase();
      const stamp = new Date().toISOString().replace(/[:.]/g, "-").slice(0, 16);
      pdf.save(`dispatch-report_${sectionKey}_${safeAgency}_${stamp}.pdf`);
    } catch (e) {
      console.error("Section PDF export failed", e);
      alert("Section PDF failed: " + (e?.message || "unknown error"));
    } finally {
      setExporting(false);
    }
  }, [exporting, homeAgency]);

  return (
    <div data-testid="reports-modal-overlay"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
      style={{
        position: "fixed", inset: 0, background: "rgba(0,0,0,0.85)",
        display: "flex", alignItems: "center", justifyContent: "center",
        zIndex: 4000, padding: 20,
      }}
    >
      <div style={{
        background: "#0d0d0d", border: "1px solid #333", borderRadius: 6,
        width: "min(1400px, 96vw)", maxHeight: "94vh", overflow: "auto",
        color: "#EEE", fontFamily: "'IBM Plex Sans', sans-serif",
      }}>
        {/* Header */}
        <div style={{
          display: "flex", alignItems: "center", justifyContent: "space-between",
          padding: "14px 20px", borderBottom: "1px solid #222",
          background: "linear-gradient(90deg,#0a2a3a,#0d0d0d)",
        }}>
          <div>
            <div style={{ fontSize: 18, fontWeight: 800, color: "#00BFFF", letterSpacing: 2 }}>
              REPORTS
            </div>
            <div data-testid="reports-agency-scope" style={{ fontSize: 11, color: "#888", marginTop: 2 }}>
              Agency: <span style={{ color: "#7AB8E8" }}>{homeAgency || "— none assigned —"}</span>
            </div>
          </div>
          <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap", justifyContent: "flex-end" }}>
            {RANGES.map((r) => (
              <button key={r.id}
                data-testid={`report-range-${r.id}`}
                onClick={() => setRange(r.id)}
                style={{
                  padding: "5px 12px", fontSize: 11, fontWeight: 700, letterSpacing: 1,
                  background: range === r.id ? "#00BFFF" : "transparent",
                  color: range === r.id ? "#000" : "#00BFFF",
                  border: "1px solid #00BFFF", borderRadius: 3, cursor: "pointer",
                  textTransform: "uppercase",
                }}
              >
                {r.label}
              </button>
            ))}
            <button
              data-testid="report-range-custom"
              onClick={() => setRange("custom")}
              style={{
                padding: "5px 12px", fontSize: 11, fontWeight: 700, letterSpacing: 1,
                background: range === "custom" ? "#00BFFF" : "transparent",
                color: range === "custom" ? "#000" : "#00BFFF",
                border: "1px solid #00BFFF", borderRadius: 3, cursor: "pointer",
                textTransform: "uppercase",
              }}
            >
              Custom
            </button>
            {range === "custom" && (
              <div data-testid="custom-range-picker"
                style={{ display: "flex", gap: 4, alignItems: "center",
                  background: "#141414", border: "1px solid #333", borderRadius: 3,
                  padding: "3px 6px",
                }}
              >
                <input data-testid="custom-range-start" type="date"
                  value={customStart} onChange={(e) => setCustomStart(e.target.value)}
                  max={customEnd || undefined}
                  style={{ background: "#0a0a0a", color: "#EEE", border: "1px solid #333",
                    padding: "2px 6px", fontSize: 11, fontFamily: "inherit", colorScheme: "dark" }}
                />
                <span style={{ color: "#666", fontSize: 11 }}>→</span>
                <input data-testid="custom-range-end" type="date"
                  value={customEnd} onChange={(e) => setCustomEnd(e.target.value)}
                  min={customStart || undefined}
                  style={{ background: "#0a0a0a", color: "#EEE", border: "1px solid #333",
                    padding: "2px 6px", fontSize: 11, fontFamily: "inherit", colorScheme: "dark" }}
                />
                <button data-testid="custom-range-apply"
                  onClick={() => setApplyTick((n) => n + 1)}
                  style={{ padding: "3px 10px", fontSize: 11, fontWeight: 700,
                    background: "#FFCC00", color: "#000", border: "1px solid #FFCC00",
                    borderRadius: 3, cursor: "pointer", letterSpacing: 1,
                  }}
                >
                  APPLY
                </button>
              </div>
            )}
            <MultiSelectFilter
              label="Call Types"
              options={filterOptions.call_types}
              selected={selectedCallTypes}
              onChange={setSelectedCallTypes}
              testid="filter-call-types"
            />
            <MultiSelectFilter
              label="Units"
              options={filterOptions.units}
              selected={selectedUnits}
              onChange={setSelectedUnits}
              testid="filter-units"
            />
            <button data-testid="report-export-pdf"
              onClick={handleExportPdf}
              disabled={exporting || loading || !data}
              style={{
                marginLeft: 4, padding: "5px 14px", fontSize: 11, fontWeight: 700, letterSpacing: 1,
                background: exporting ? "#333" : "#FFCC00",
                color: exporting ? "#888" : "#000",
                border: "1px solid #FFCC00", borderRadius: 3,
                cursor: exporting || loading || !data ? "not-allowed" : "pointer",
                textTransform: "uppercase",
              }}
            >
              {exporting ? "Exporting…" : "Download PDF"}
            </button>
            <button data-testid="reports-close" onClick={onClose}
              style={{
                marginLeft: 8, padding: "5px 14px", fontSize: 11, fontWeight: 700,
                background: "transparent", color: "#888", border: "1px solid #333",
                borderRadius: 3, cursor: "pointer",
              }}
            >
              CLOSE
            </button>
          </div>
        </div>

        {/* Body */}
        <div ref={printRef} style={{ padding: 20 }}>
          {err && (
            <div data-testid="reports-error" style={{ color: "#FF5555", background: "#2a0a0a", border: "1px solid #FF5555", padding: 10, borderRadius: 4, marginBottom: 16, fontSize: 13 }}>
              {err}
            </div>
          )}
          {loading && <div style={{ color: "#888", fontSize: 12, marginBottom: 12 }}>Loading…</div>}

          {orphanCount > 0 && (
            <div data-testid="orphan-banner"
              style={{
                background: "#2a1e05", border: "1px solid #FFCC00",
                borderRadius: 4, padding: "10px 14px", marginBottom: 14,
                display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12,
              }}
            >
              <div style={{ fontSize: 12, color: "#FFCC00" }}>
                <strong>{orphanCount}</strong> old call(s) in the database have no agency stamped
                (predate per-job agency tracking) — they're not appearing on this report. Click to attribute them to <strong>{homeAgency}</strong>.
              </div>
              <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                {backfillMsg && (
                  <span data-testid="orphan-msg" style={{ fontSize: 11, color: backfillMsg.startsWith("Error") ? "#FF5555" : "#7AB8E8" }}>
                    {backfillMsg}
                  </span>
                )}
                <button
                  data-testid="orphan-backfill-btn"
                  onClick={runBackfill}
                  style={{
                    background: "#FFCC00", color: "#000", border: "1px solid #FFCC00",
                    borderRadius: 3, padding: "5px 14px", fontSize: 11, fontWeight: 800,
                    letterSpacing: 1, cursor: "pointer", textTransform: "uppercase",
                  }}
                >
                  Attribute to {homeAgency ? "My Agency" : "…"}
                </button>
              </div>
            </div>
          )}

          {data && data.total_calls === 0 && audit && (
            <div data-testid="agency-audit-panel"
              style={{
                background: "#2a0d16", border: "1px solid #FF5555",
                borderRadius: 4, padding: "12px 14px", marginBottom: 14,
              }}
            >
              <div style={{ fontSize: 13, fontWeight: 700, color: "#FF9999", marginBottom: 8, letterSpacing: 1 }}>
                0 calls under &ldquo;{homeAgency}&rdquo; — but the database contains {audit.total_jobs} total jobs. Below is every agency label present in the DB. If your history lives under a different label, click <em>Rename to My Agency</em> next to it.
              </div>
              <table data-testid="agency-audit-table" style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
                <thead>
                  <tr style={{ color: "#CCC", textAlign: "left" }}>
                    <th style={{ padding: "6px 8px", borderBottom: "1px solid #444" }}>Agency Label (as stored)</th>
                    <th style={{ padding: "6px 8px", borderBottom: "1px solid #444", textAlign: "right" }}>Jobs</th>
                    <th style={{ padding: "6px 8px", borderBottom: "1px solid #444", textAlign: "right" }}>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {(audit.agencies || []).map((row) => (
                    <tr key={row.agency}>
                      <td style={{ padding: "6px 8px", borderBottom: "1px solid #222", color: "#EEE", fontFamily: "monospace" }}>
                        {row.agency === "(null)" || row.agency === "" ? <em style={{ color: "#888" }}>(empty)</em> : row.agency}
                      </td>
                      <td style={{ padding: "6px 8px", borderBottom: "1px solid #222", color: "#00BFFF", textAlign: "right", fontWeight: 700 }}>
                        {row.n}
                      </td>
                      <td style={{ padding: "6px 8px", borderBottom: "1px solid #222", textAlign: "right" }}>
                        {row.agency !== homeAgency && (
                          <button
                            data-testid={`agency-audit-rename-${(row.agency || "empty").replace(/[^a-z0-9]+/gi, "-")}`}
                            onClick={() => runRename(row.agency === "(null)" ? "" : row.agency)}
                            style={{
                              background: "#FFCC00", color: "#000", border: "1px solid #FFCC00",
                              borderRadius: 3, padding: "3px 10px", fontSize: 10, fontWeight: 800,
                              letterSpacing: 1, cursor: "pointer", textTransform: "uppercase",
                            }}
                          >
                            Rename to My Agency
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Iter 113 — Summary strip: removed "Avg Response" tile per
              user request. */}
          <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 12, marginBottom: 20 }}>
            <SummaryTile label="Total Calls" value={data?.total_calls ?? 0} testid="summary-total" />
            <SummaryTile label="Unique Units" value={(data?.by_unit || []).length} testid="summary-unique-units" />
            <SummaryTile label="Call Types" value={(data?.by_call_type || []).length} testid="summary-call-types" />
          </div>

          <Section
            title="Call Type Breakdown"
            sectionRef={sectionRefs.calltypes}
            onExportPdf={() => handleExportSectionPdf("calltypes", "Call Type Breakdown", ctRangeLabel)}
            right={
              <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                <SectionRangePicker value={ctSectionRange} onChange={setCtSectionRange} testid="ct-section-range" />
                <span style={{ fontSize: 10, color: "#888", letterSpacing: 1 }}>UNIT:</span>
                <SectionPicker
                  value={ctSectionUnit} onChange={setCtSectionUnit}
                  options={filterOptions.units}
                  testid="ct-section-unit"
                />
                <SectionPicker
                  value={ctPick} onChange={setCtPick}
                  options={(ctSrc?.by_call_type || []).map((r) => r.name)}
                  testid="ct-picker"
                />
                {ctPick === "ALL" && <ChartPicker value={ctType} onChange={setCtType} />}
              </div>
            }
            testid="section-call-types"
          >
            {ctPick === "ALL" ? (
              <CategoricalChart
                data={ctSrc?.by_call_type || []}
                chartType={ctType}
                testid="chart-call-types"
              />
            ) : (
              <MatrixTable
                rows={matrix.call_type_matrix.filter((r) => r.name === ctPick)}
                labelHeader="Call Type"
                testid="matrix-call-types"
              />
            )}
          </Section>

          <Section
            title="Calls Assigned per Unit"
            sectionRef={sectionRefs.byunit}
            onExportPdf={() => handleExportSectionPdf("byunit", "Calls Assigned per Unit", unitRangeLabel)}
            right={
              <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                <SectionRangePicker value={unitSectionRange} onChange={setUnitSectionRange} testid="unit-section-range" />
                <SectionPicker
                  value={unitPick} onChange={setUnitPick}
                  options={filterOptions.units}
                  testid="unit-picker"
                />
                {unitPick === "ALL" && <ChartPicker value={unitType} onChange={setUnitType} />}
              </div>
            }
            testid="section-by-unit"
          >
            {unitPick === "ALL" ? (
              <CategoricalChart data={unitSrc?.by_unit || []} chartType={unitType} testid="chart-by-unit" />
            ) : (
              <MatrixTable
                rows={matrix.unit_matrix.filter((r) => r.name === unitPick)}
                labelHeader="Unit"
                testid="matrix-by-unit"
              />
            )}
          </Section>

          <Section
            title="Average Response Time (dispatched → on-scene)"
            sectionRef={sectionRefs.response}
            onExportPdf={() => handleExportSectionPdf("response", "Average Response Time", respRangeLabel)}
            right={
              <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                <SectionRangePicker value={respSectionRange} onChange={setRespSectionRange} testid="resp-section-range" />
                <MultiSelectFilter
                  label="Units"
                  options={filterOptions.units}
                  selected={respSectionUnits}
                  onChange={setRespSectionUnits}
                  testid="resp-section-units"
                />
              </div>
            }
            testid="section-response"
          >
            <ResponseTimeBlock
              src={respSrc}
              allUnits={filterOptions.units}
              selectedUnits={respSectionUnits}
              testid="response-block"
            />
          </Section>

          <Section
            title={`Heatmap — ${data?.heatmap_points?.length || 0} located calls`}
            sectionRef={sectionRefs.heatmap}
            onExportPdf={() => handleExportSectionPdf("heatmap", "Call Location Heatmap", topRangeLabel)}
            testid="section-heatmap"
          >
            <div style={{ height: 420, borderRadius: 4, overflow: "hidden", border: "1px solid #222" }}>
              <MapContainer
                center={[40.6892, -73.9442]}
                zoom={12}
                style={{ height: "100%", width: "100%", background: "#000" }}
                data-testid="heatmap-container"
              >
                <TileLayer
                  attribution='&copy; <a href="https://stadiamaps.com/">Stadia Maps</a>'
                  url={STADIA_TILE_URL}
                />
                <HeatmapLayer points={data?.heatmap_points || []} />
              </MapContainer>
            </div>
          </Section>
        </div>
      </div>
    </div>
  );
}

function SummaryTile({ label, value, testid }) {
  return (
    <div data-testid={testid}
      style={{
        background: "#141414", border: "1px solid #222", borderRadius: 4,
        padding: "12px 16px",
      }}
    >
      <div style={{ fontSize: 10, color: "#888", letterSpacing: 2, textTransform: "uppercase" }}>{label}</div>
      <div style={{ fontSize: 22, color: "#00BFFF", fontWeight: 800, marginTop: 2 }}>{value}</div>
    </div>
  );
}

function Section({ title, right, children, testid, sectionRef, onExportPdf }) {
  return (
    <div data-testid={testid} style={{ marginBottom: 22 }} ref={sectionRef}>
      <div style={{
        display: "flex", alignItems: "center", justifyContent: "space-between",
        padding: "6px 10px", background: "#141414", border: "1px solid #222",
        borderBottom: "none", borderRadius: "4px 4px 0 0",
      }}>
        <div style={{ fontSize: 12, fontWeight: 700, color: "#CCC", letterSpacing: 1 }}>{title}</div>
        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          {right}
          {onExportPdf && (
            <button
              data-testid={`${testid}-pdf`}
              onClick={onExportPdf}
              style={{
                padding: "3px 10px", fontSize: 10, fontWeight: 800, letterSpacing: 1,
                background: "#FFCC00", color: "#000", border: "1px solid #FFCC00",
                borderRadius: 3, cursor: "pointer", textTransform: "uppercase",
              }}
            >
              PDF
            </button>
          )}
        </div>
      </div>
      <div style={{ padding: 10, background: "#0a0a0a", border: "1px solid #222", borderRadius: "0 0 4px 4px" }}>
        {children}
      </div>
    </div>
  );
}


function SectionPicker({ value, onChange, options, testid }) {
  return (
    <select
      data-testid={`${testid}-select`}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      style={{
        background: value === "ALL" ? "transparent" : "rgba(0,191,255,0.15)",
        color: "#00BFFF",
        border: "1px solid #00BFFF",
        borderRadius: 3,
        padding: "4px 10px",
        fontSize: 11, fontWeight: 700, letterSpacing: 1,
        cursor: "pointer",
        textTransform: "uppercase",
        fontFamily: "inherit",
        maxWidth: 220,
      }}
    >
      <option value="ALL" style={{ color: "#000" }}>ALL</option>
      {options.map((opt) => (
        <option key={opt} value={opt} style={{ color: "#000" }}>{opt}</option>
      ))}
    </select>
  );
}


function MatrixTable({ rows, labelHeader, testid }) {
  const row = (rows && rows[0]) || { name: "", day: 0, week: 0, month: 0, year: 0 };
  const cells = [
    { label: "Today", value: row.day },
    { label: "This Week", value: row.week },
    { label: "This Month", value: row.month },
    { label: "This Year", value: row.year },
  ];
  return (
    <div data-testid={testid} style={{ padding: "4px 6px 10px 6px" }}>
      <div style={{ fontSize: 11, color: "#888", letterSpacing: 2, textTransform: "uppercase", marginBottom: 6 }}>
        {labelHeader}: <span style={{ color: "#7AB8E8" }}>{row.name || "—"}</span>
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 10 }}>
        {cells.map((c) => (
          <div key={c.label} data-testid={`${testid}-${c.label.toLowerCase().replace(/\s+/g, "-")}`}
            style={{
              background: "#141414", border: "1px solid #222", borderRadius: 4,
              padding: "16px 18px", textAlign: "center",
            }}
          >
            <div style={{ fontSize: 30, color: "#00BFFF", fontWeight: 800, lineHeight: 1 }}>{c.value}</div>
            <div style={{ fontSize: 10, color: "#888", letterSpacing: 2, textTransform: "uppercase", marginTop: 8 }}>
              {c.label}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
